/* istanbul ignore file */
import { BladeBaseProps } from '@commblades/commblades';

export type dropdownOptions = {
    value: string,
    key: string,
}

export type AmountValidation = {
    defaultErrorMessage: string,
    minValue: number,
    minValueMessage: string,
    maxValue?: number,
    maxValueMessage?: string,
    negativeValueMessage: string,
}

export type AmountData = {
    amount: number,
    frequency?: string,
    beforeTax?: boolean,
}

export interface YourFinanceErrorMessage {
    netIncome?: string,
    rent?: string,
    repayment?: string,
    creditLimit?: string,
}

export type AmountMapInfo = Record<string, AmountData>;
export type YourFinanceDetail = {
    bladeName: string,
    loanPurposeId: number,
    subLoanPurposeId: number,
    dependants: string,
    amountData: AmountMapInfo
};
export const DefaultYourFinanceDetail: YourFinanceDetail = {
    bladeName: 'debt-consolidation',
    loanPurposeId: -9999,
    subLoanPurposeId: -9999,
    dependants: '',
    amountData: {
        netIncome: {
            amount: 0,
            frequency: 'Yearly',
            beforeTax: true,
        },
        rent: {
            amount: 0,
            frequency: 'Weekly',
        },
        repayment: {
            amount: 0,
            frequency: 'Monthly',
        },
        creditLimit: {
            amount: 0,
        },
    },
};

export type LoanProductDetail = {
    loanProductId: string,
    interestRate: number
}

export type YourFinanceSubmit = {
    bladeName: string,
    incomeDetails: {
        taxType: string,
        totalIncome: number,
        frequency: string
    }
    productType: string,
    loanPurposeId: number,
    expensesDetails:{
       rent: number,
       rentFrequency: string,
       existingDebt: number,
       existingDebtFrequency: string,
       creditCardLimits: number
    }
    totalDependents: number,
};

export const DefaultYourFinanceSubmit: YourFinanceSubmit = {
    bladeName: 'debt-consolidation',
    incomeDetails: {
        taxType: 'BeforeTax',
        totalIncome: 0,
        frequency: 'Weekly',
    },
    productType: 'Standard',
    loanPurposeId: -9999,
    expensesDetails: {
        rent: 0,
        rentFrequency: 'Weekly',
        existingDebt: 0,
        existingDebtFrequency: 'Weekly',
        creditCardLimits: 0,
    },
    totalDependents: 0,
};
export const DefaultYourFinanceData = {
    heading: 'How much can you borrow for debt consolidation?',
    description: 'Give us a quick overview of your current financial circumstances.',
    accordionHeader: 'I have other existing debt',
    infoLabel: 'We can give you a more accurate estimate of your borrowing power if you provide detailed information about any existing debt.',
    incomeForm: {
        label: 'How much do you earn?',
        frequencyDropdownOptions: [
            {
                value: '101',
                key: 'Yearly',
            },
            {
                value: '102',
                key: 'Monthly',
            },
            {
                value: '103',
                key: 'Fortnightly',
            },
            {
                value: '104',
                key: 'Weekly',
            },
        ],
        taxDropdownOptions: [
            {
                value: '101',
                key: 'Before tax',
            },
            {
                value: '102',
                key: 'After tax',
            },
        ],
        hintLabel: 'Enter total amount in dollars',
    },
    financiallySupportForm: {
        label: 'How many people do you financially support?',
        DropdownOptions: [
            {
                value: '101',
                key: '0',
            },
            {
                value: '102',
                key: '1',
            },
            {
                value: '103',
                key: '2',
            },
            {
                value: '104',
                key: '3',
            },
            {
                value: '105',
                key: '4',
            },
            {
                value: '106',
                key: '5',
            },
            {
                value: '107',
                key: '6',
            },
            {
                value: '108',
                key: '6+',
            },
        ],
    },
    rentForm: {
        label: 'How much do you pay for rent/mortage?',
        inputPlaceHolder: 'Enter total amount',
        DropdownOptions: [
            {
                value: '102',
                key: 'Monthly',
            },
            {
                value: '103',
                key: 'Fortnightly',
            },
            {
                value: '104',
                key: 'Weekly',
            },
        ],
        hintLabel: 'No mortgage or rent? Leave it blank',
    },
    repaymentForm: {
        dropdownOptions: [
            {
                key: '1',
                value: 'Bi-monthly',
            },
            {
                key: '2',
                value: 'Monthly',
            },
            {
                key: '3',
                value: 'Yearly',
            },
            {
                key: '4',
                value: 'Weekly',
            },
            {
                key: '5',
                value: 'Quarterly',
            },
            {
                key: '6',
                value: 'Semi-annually',
            },
        ],
        label: 'How much is your total ongoing debts repayment?',
        inputPlaceHolder: 'Enter total amount',
        hintLabel: 'Do not include the debt that you intend to consolidate',
    },
    creditLimitForm: {
        label: 'What is your ongoing combined credit limit?',
        inputPlaceHolder: 'Enter',
        hintLabel: 'Do not include the credit limit that you intend to consolidate',
    },
    confirmButtonText: 'Estimate',
    validations: {
        hardValidations: {
            income: {
                minValue: 0.01,
                minValueMessage: 'income amount is too low',
                maxValue: 9999999,
                maxValueMessage: 'income amount exceeds limits',
                defaultErrorMessage: '{Enter amount validation required placeholder}',
                negativeValueMessage: 'Amount must be positive',
            },
            rent: {
                minValue: 0.01,
                minValueMessage: 'rent amount is too low',
                maxValue: 9999999,
                maxValueMessage: 'rent amount exceeds limits',
                defaultErrorMessage: '{Enter amount validation required placeholder}',
                negativeValueMessage: 'Amount must be positive',
            },
            repayment: {
                minValue: 0.01,
                minValueMessage: '{repayment amount is too low}',
                maxValue: 9999999,
                maxValueMessage: 'repayment amount exceeds limits',
                defaultErrorMessage: '{Enter amount validation required placeholder}',
                negativeValueMessage: '{Amount must be positive}',
            },
            creditLimit: {
                minValue: 0.01,
                minValueMessage: '{credit limit amount is too low}',
                maxValue: 9999999,
                maxValueMessage: 'credit limit amount exceeds limits',
                defaultErrorMessage: '{Enter amount validation required placeholder}',
                negativeValueMessage: '{Amount must be positive}',
            },
        },
    },
};
export type YourFinanceData = typeof DefaultYourFinanceData;

export const DefaultBorrowingAmountWithLoanTerms = {
    1: {
        amount: 20243,
        isValid: true,
        errorMessage: '',
    },
    2: {
        amount: 37511,
        isValid: true,
        errorMessage: '',
    },
    3: {
        amount: 50000,
        isValid: true,
        errorMessage: '',
    },
    4: {
        amount: 50000,
        isValid: true,
        errorMessage: '',
    },
    5: {
        amount: 50000,
        isValid: true,
        errorMessage: '',
    },
    6: {
        amount: 50000,
        isValid: true,
        errorMessage: '',
    },
    7: {
        amount: 50000,
        isValid: true,
        errorMessage: '',
    },
};

export const DefaultYourFinanceResponse = {
    errorMessage: '',
    estimateAmount: 0,
    loanType: 'VariableRate',
    interestRate: 16.000,
    comparisionRate: 16.83,
    estimation: [
        {
            loanType: 'VariableRate',
            borrowAmount: 31979,
            cappedAmount: 50000,
            repaymentAmount: 159,
            interestRate: 16.000,
            comparisionRate: 16.83,
            frequency: 'Weekly',
            borrowingAmountWithLoanTerms: {
                1: {
                    amount: 20243,
                    isValid: true,
                    errorMessage: '',
                },
                2: {
                    amount: 37511,
                    isValid: true,
                    errorMessage: '',
                },
                3: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                4: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                5: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                6: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                7: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
            },
            isValid: true,
            interestDetails: {
                representationRate: 8.99,
                comparisonRate: 10.38,
                interestRateRange: [
                    6.99,
                    13.49,
                ],
                comparisonRateRange: [
                    8.40,
                    14.83,
                ],
            },
        },
        {
            loanType: 'FixedRate',
            borrowAmount: 31979,
            cappedAmount: 50000,
            repaymentAmount: 159,
            interestRate: 16.000,
            comparisionRate: 17.31,
            frequency: 'Weekly',
            borrowingAmountWithLoanTerms: {
                1: {
                    amount: 20243,
                    isValid: true,
                    errorMessage: '',
                },
                2: {
                    amount: 37511,
                    isValid: true,
                    errorMessage: '',
                },
                3: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                4: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                5: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                6: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                7: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
            },
            isValid: true,
            interestDetails: {
                representationRate: 8.99,
                comparisonRate: 10.38,
                interestRateRange: [
                    6.99,
                    13.49,
                ],
                comparisonRateRange: [
                    8.40,
                    14.83,
                ],
            },
        },
        {
            loanType: 'SecuredLoan',
            borrowAmount: 39491,
            cappedAmount: 100000,
            repaymentAmount: 159,
            interestRate: 8.990,
            comparisionRate: 9.88,
            frequency: 'Weekly',
            borrowingAmountWithLoanTerms: {
                1: {
                    amount: 20243,
                    isValid: true,
                    errorMessage: '',
                },
                2: {
                    amount: 37511,
                    isValid: true,
                    errorMessage: '',
                },
                3: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                4: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                5: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                6: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
                7: {
                    amount: 50000,
                    isValid: true,
                    errorMessage: '',
                },
            },
            isValid: true,
            interestDetails: {
                representationRate: 8.99,
                comparisonRate: 10.38,
                interestRateRange: [
                    6.99,
                    13.49,
                ],
                comparisonRateRange: [
                    8.40,
                    14.83,
                ],
            },
        },
    ],
};
export type BorrowingAmountWithLoanTerms = typeof DefaultBorrowingAmountWithLoanTerms;
export type YourFinanceResponse = typeof DefaultYourFinanceResponse;
export interface YourFinanceBladeProps extends BladeBaseProps {
    isShowExistingDebt?: boolean;
    bladeId?: string;
    isCollapseDebt?: boolean;
    isFrequencyDrowdownAscent?: boolean;
}

export interface YourFinanceState {
    data: YourFinanceData,
    submitData: YourFinanceSubmit,
    submitUrl?: string,
    isSubmitDataReady: boolean,
}
